import { createDataSource } from '@adv-frontend/reactions';
import { UnexpectedError } from '@adv-frontend/error';
import type { IterableDataSource } from '@adv-frontend/reactions';
import type { QueryArgFrom, ResultTypeFrom } from '../rtk-query-types';
import type { AnyApi } from '../buildCreateApi';
import type { GetStoreFn } from '../types';
import type { AnyQueryDefinition, AnyEndpointQuery } from './types';

// Набор дополнительных методов эндпоинта, которые добавляются в RTK Query.
export type EndpointQuery<Definition extends AnyQueryDefinition> = {
    /**
     * @soft-deprecated Реализация метода скоро будет заменена на потоки в DIRECT-288556.
     */
    query: (
        queryArgs: QueryArgFrom<Definition>,
        options?: QueryOptions
    ) => IterableDataSource<
        Promise<ResultTypeFrom<Definition>>,
        Promise<ResultTypeFrom<Definition>>,
        ResultTypeFrom<Definition>
    >;
};

// Параметры для `buildEndpointMutate()`.
type BuildQueryOptions = {
    api: AnyApi;
    endpointName: string;
    getStore: GetStoreFn;
};

// Параметры запроса endpoint.query(arg, options)
type QueryOptions = {
    /**
     * Флаг, который позволяет сделать запрос даже в том случае, если в кеше есть данные.
     */
    forceRefetch?: boolean;

    /**
     * Возраст данных в кеше (в секундах), после которого нужно перезапросить данные.
     * Если в кеше есть достаточно свежые данные, то функция вернет закешированный результат без запроса в бекенд.
     */
    refetchIfOlderThan?: number;
};

type QueryOptionsInternal = QueryOptions & {
    /**
     * Нужно ли создавать подписку на кэш в сторе
     */
    subscribe?: boolean;
};

// Ссылка на объект, в который нужно записать значение промиса для последующего синхронного использования.
type PromiseValueRef<T> = {
    /* Значение промиса */
    value: T;
    /* Признак того, что промис был успешно разрешен */
    fulfilled: boolean;
};

type WaitQueryOptions = {
    endpoint: AnyEndpointQuery;
    queryArgs: QueryArgFrom<AnyQueryDefinition>;
    options?: QueryOptionsInternal;
    getStore: GetStoreFn;
    valueRef: PromiseValueRef<unknown>;
};

function createWaitQueryPromise({ endpoint, queryArgs, options, getStore, valueRef }: WaitQueryOptions) {
    const initiatePromise = getStore().dispatch(
        endpoint.initiate(queryArgs, {
            forceRefetch: options?.forceRefetch || options?.refetchIfOlderThan,
        })
    );

    const waitQueryPromise = initiatePromise.then(() => {
        return new Promise((resolve, reject) => {
            // Вызов endpoint.initiate возвращает промис, который достает данные из кэша.
            // Если endpoint.initiate инициирует запрос за новыми данными, то нам нужно дождаться именно их.
            // Понять, есть ли в текущий момент запрос, можно только из селектора эндпоинта.
            const dataSelector = endpoint.select(queryArgs);
            const previousState = dataSelector(getStore().getState());

            function handleState(state: ReturnType<typeof dataSelector>) {
                if (state.isSuccess) {
                    valueRef.fulfilled = true;
                    valueRef.value = state.data;
                    return resolve(state.data);
                }

                if (state.isError) {
                    return reject(state.error);
                }
                return reject(
                    new UnexpectedError('Неожиданное состояние запроса за данными', {
                        additional: {
                            endpointName: endpoint.name,
                            status: state.status,
                        },
                        cause: null,
                    })
                );
            }

            if (!previousState.isLoading) {
                return handleState(previousState);
            }

            const unsubscribe = getStore().subscribe(() => {
                const currentState = dataSelector(getStore().getState());

                if (currentState === previousState || currentState.isLoading) {
                    return;
                }

                unsubscribe();
                return handleState(currentState);
            });
        }).finally(() => {
            // По-умолчанию, подписка не нужна,
            // чтобы данные не остались в кеше на вечно из-за лишней подписки.
            // Поэтому подписка удаляется.
            // Предзагруженные данные останутся в сторе на время keepUnusedDataFor, который
            // указан в настройках endpoint (по умолчанию 60 сек).
            //
            // Подписка нужна, например, при использовании в dataSource реакций
            if (!options?.subscribe) {
                initiatePromise.unsubscribe();
            }
        });
    });

    return Object.assign(waitQueryPromise, {
        unsubscribe: initiatePromise.unsubscribe,
    });
}

export function buildEndpointQuery({
    api,
    endpointName,
    getStore,
}: BuildQueryOptions): EndpointQuery<AnyQueryDefinition>['query'] {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    const endpoint = api.endpoints[endpointName] as AnyEndpointQuery;

    function query(queryArgs: QueryArgFrom<AnyQueryDefinition>, options?: QueryOptionsInternal) {
        // Сохраняем результат выполнения промиса в ссылку, чтобы иметь к нему синхронный доступ внутри итератора.
        const valueRef: PromiseValueRef<unknown> = { value: undefined, fulfilled: false };

        const waitQueryPromise = createWaitQueryPromise({
            endpoint,
            getStore,
            queryArgs,
            options,
            valueRef,
        });

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
        return Object.assign(waitQueryPromise, {
            [Symbol.iterator]: function* () {
                yield waitQueryPromise;

                /**
                 * Когда вызывается `yield waitQueryPromise` управление передается в реакцию.
                 * Там есть await waitQueryPromise
                 * Если waitQueryPromise выбросит ошибку, то она будет передана в *data а сюда выполнение не попадет
                 * Поэтому всегда ожидаем, что промис к этому моменту всегда в fulfilled состоянии.
                 */
                if (!valueRef.fulfilled) {
                    throw new UnexpectedError('Query promise has not been resolved', { cause: null });
                }

                return valueRef.value;
            },
        }) as unknown as IterableDataSource<
            Promise<ResultTypeFrom<AnyQueryDefinition>>,
            Promise<ResultTypeFrom<AnyQueryDefinition>>,
            ResultTypeFrom<AnyQueryDefinition>
        >;
    }

    return createDataSource({
        // waitQueryPromise - это промис созданный в createWaitQueryPromise
        // в него записана функция отписки от кэша
        createSubscribe(waitQueryPromise, queryArgs) {
            const dataSelector = endpoint.select(queryArgs);

            let prevValue: unknown;

            return notify => {
                // Подписка создается до получения ответа. При получении ответа данные в сторе меняются,
                // что приводит к оповещению ПОДПИСЧИКОВ СТОРА.
                // Поэтому, игнорируем оповещения до разрешения waitQueryPromise
                let isInitiated = false;

                if (waitQueryPromise instanceof Promise) {
                    waitQueryPromise
                        .catch(() => {})
                        .finally(() => {
                            isInitiated = true;
                            const data = dataSelector(getStore().getState());
                            // Записываем в prevValue текущие данные,
                            // чтобы избежать дублирования запусков с теми же данными
                            prevValue = data.isSuccess ? data.data : data.error;
                        });
                } else {
                    console.error(
                        new UnexpectedError('Неожиданный тип waitQueryPromise', {
                            cause: undefined,
                            additional: { type: typeof waitQueryPromise },
                        })
                    );
                }

                const store = getStore();

                const subscriptionToStore = () => {
                    const state = dataSelector(getStore().getState());

                    // Игнорируем все изменения в сторе, кроме завершения запроса
                    if (!isInitiated || !(state.isSuccess || state.isError)) {
                        return;
                    }

                    const dataOrError = state.isSuccess ? state.data : state.error;

                    // Запускаем реакции только если данные изменились
                    if (prevValue !== dataOrError) {
                        prevValue = dataOrError;
                        notify();
                    }
                };

                const unsubscribeStore = store.subscribeBatched(subscriptionToStore);

                return () => {
                    unsubscribeStore();
                    if (
                        typeof waitQueryPromise === 'object' &&
                        waitQueryPromise &&
                        'unsubscribe' in waitQueryPromise &&
                        typeof waitQueryPromise.unsubscribe === 'function'
                    ) {
                        waitQueryPromise.unsubscribe();
                    } else {
                        console.error(
                            new UnexpectedError('Неожиданный тип waitQueryPromise', {
                                cause: undefined,
                                additional: { type: typeof waitQueryPromise },
                            })
                        );
                    }
                };
            };
        },

        getValue: query,

        getValueInReaction(queryArgs, options) {
            return query(queryArgs, { ...options, subscribe: true });
        },
    });
}
